import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Copy, Download, FileText,
         Loader2, Users, Edit, Save, ArrowLeftRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── List page ─────────────────────────────────────────────────────
export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState('invoices');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [inv, sup] = await Promise.all([
        axios.get(`${API}/invoices`, { withCredentials: true }),
        axios.get(`${API}/invoices/suppliers`, { withCredentials: true }),
      ]);
      setInvoices(inv.data.invoices || []);
      setSuppliers(sup.data.suppliers || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [picking, setPicking] = useState(false);
  const newInvoice = () => {
    if (suppliers.length < 2) { alert('Please add a company first.'); setTab('suppliers'); return; }
    setPicking(true);
  };
  const createInvoice = async (fromId, toId) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/invoices`,
        { supplier_id: fromId, recipient_id: toId, line_items: [] },
        { withCredentials: true });
      navigate(`/invoices/${r.data.invoice_number}`);
    } catch (e) { alert(e?.response?.data?.detail || 'Create failed'); }
    finally { setCreating(false); }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/invoices/export.csv`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'invoices-all.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e?.response?.data?.detail || 'CSV export failed'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-5 md:p-8">
        <Link to="/" className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#0d2644' }}>Invoicing</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Bill i3 or bill others · Pick who sends and who receives · Auto-totals · PDF + CSV export
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={newInvoice} disabled={creating} data-testid="new-invoice"
              className="text-sm font-semibold inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white shadow-sm">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New invoice
            </button>
            <button onClick={downloadCsv} data-testid="csv-export"
              className="text-sm font-semibold inline-flex items-center gap-2 px-4 py-2 rounded-lg border">
              <Download className="w-4 h-4" /> All invoices CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1 border-b mb-4">
          {[['invoices', 'Invoices', FileText], ['suppliers', 'Companies & people', Users]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-2 border-b-2 transition-colors ${
                tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-muted-foreground'}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>

        {loading && <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>}

        {!loading && tab === 'invoices' && <InvoicesTable invoices={invoices} suppliers={suppliers} />}
        {!loading && tab === 'suppliers' && <SuppliersPanel suppliers={suppliers} onChange={load} />}
        {picking && <NewInvoiceDialog parties={suppliers} busy={creating}
          onClose={() => setPicking(false)} onCreate={createInvoice} />}
      </div>
    </div>
  );
}

function InvoicesTable({ invoices, suppliers }) {
  const supMap = useMemo(() => Object.fromEntries(suppliers.map(s => [s.supplier_id, s])), [suppliers]);
  if (!invoices.length) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center">
        <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm text-muted-foreground">No invoices yet. Tap <strong>New invoice</strong> to create your first one.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border bg-card p-2 overflow-x-auto" data-testid="invoices-list">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
          <tr>
            <th className="text-left py-2 px-3">Invoice No</th>
            <th className="text-left py-2 px-3">Date</th>
            <th className="text-left py-2 px-3">From</th>
            <th className="text-left py-2 px-3">To</th>
            <th className="text-right py-2 px-3">Total</th>
            <th className="text-left py-2 px-3">Status</th>
            <th className="py-2 px-3"></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(i => (
            <tr key={i.invoice_number} className="border-t hover:bg-muted/40 transition-colors" data-testid={`row-${i.invoice_number}`}>
              <td className="py-2.5 px-3 font-mono text-xs font-semibold whitespace-nowrap">{i.invoice_number}</td>
              <td className="py-2.5 px-3 whitespace-nowrap">{i.invoice_date}</td>
              <td className="py-2.5 px-3" data-testid={`row-from-${i.invoice_number}`}>{supMap[i.supplier_id]?.company_name || i.supplier_snapshot?.company_name || '—'}</td>
              <td className="py-2.5 px-3" data-testid={`row-to-${i.invoice_number}`}>{supMap[i.recipient_id || 'sup_practice']?.company_name || i.recipient_snapshot?.company_name || '—'}</td>
              <td className="py-2.5 px-3 text-right font-semibold">${fmt(i.total)}</td>
              <td className="py-2.5 px-3">
                <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                  style={{
                    background: i.status === 'paid' ? '#dcfce7' : i.status === 'sent' ? '#dbeafe' : '#f1f5f9',
                    color:      i.status === 'paid' ? '#15803d' : i.status === 'sent' ? '#1e40af' : '#475569',
                  }}>{i.status}</span>
              </td>
              <td className="py-2.5 px-3 text-right">
                <Link to={`/invoices/${i.invoice_number}`} className="text-xs font-semibold px-2.5 py-1 rounded border inline-flex items-center gap-1"
                  data-testid={`open-${i.invoice_number}`}>
                  <Edit className="w-3 h-3" /> Open
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SuppliersPanel({ suppliers, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const emptySup = {
    company_name: '', doctor_name: '', address_line1: '', address_line2: '',
    city: '', state: '', postcode: '', country: 'Australia', abn: '',
    gst_rate: 0.10, bank_name: '', bank_account_name: '', bsb: '',
    account_number: '', swift: '', iban: '', email: '', notes: '', invoice_prefix: '',
  };
  return (
    <div data-testid="suppliers-panel">
      <div className="flex justify-end mb-3">
        <button onClick={() => { setCreating(true); setEditing(emptySup); }}
          data-testid="new-supplier"
          className="text-sm font-semibold inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white">
          <Plus className="w-4 h-4" /> New company / person
        </button>
      </div>
      {suppliers.length === 0 && !editing && (
        <div className="rounded-xl border bg-card p-10 text-center">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm text-muted-foreground">No companies saved yet. Tap <strong>New company / person</strong>.</p>
        </div>
      )}
      {suppliers.length > 0 && (
        <div className="rounded-xl border bg-card p-2">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 px-3">Company</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Invoice no.</th>
                <th className="text-left py-2 px-3">Doctor</th>
                <th className="text-left py-2 px-3">Country</th>
                <th className="text-right py-2 px-3">GST %</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(s => (
                <tr key={s.supplier_id} className="border-t hover:bg-muted/40" data-testid={`sup-row-${s.supplier_id}`}>
                  <td className="py-2 px-3 font-semibold">
                    {s.company_name}
                    {s.is_practice && <span data-testid="sup-practice-badge"
                      className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">i3 · you</span>}
                  </td>
                  <td className="py-2 px-3">
                    <span data-testid={`sup-type-${s.supplier_id}`}
                      className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {s.type || 'teleradiologist'}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs" data-testid={`sup-prefix-${s.supplier_id}`}>{s.invoice_prefix || '—'}-YYYY-00001</td>
                  <td className="py-2 px-3">{s.doctor_name || '—'}</td>
                  <td className="py-2 px-3">{s.country}</td>
                  <td className="py-2 px-3 text-right">{Number(((s.gst_rate || 0) * 100).toFixed(2))}%</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => { setCreating(false); setEditing(s); }} data-testid={`edit-sup-${s.supplier_id}`}
                      className="text-xs font-semibold px-2 py-1 rounded border">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <SupplierEditor sup={editing} creating={creating}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }} />
      )}
    </div>
  );
}

function SupField({ label, k, s, setS, type='text', ...extra }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Input type={type} value={s[k] ?? ''} onChange={e => setS({ ...s, [k]: e.target.value })}
        data-testid={`sup-${k}`} {...extra} />
    </label>
  );
}

function SupplierEditor({ sup, creating, onClose, onSaved }) {
  const [s, setS] = useState(sup);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...s, gst_rate: parseFloat(s.gst_rate) };
      if (creating) await axios.post(`${API}/invoices/suppliers`, payload, { withCredentials: true });
      else          await axios.patch(`${API}/invoices/suppliers/${s.supplier_id}`, payload, { withCredentials: true });
      onSaved();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!s.supplier_id || !window.confirm(`Delete ${s.company_name}?`)) return;
    await axios.delete(`${API}/invoices/suppliers/${s.supplier_id}`, { withCredentials: true });
    onSaved();
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="supplier-editor">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-3" style={{ color: '#0d2644' }}>{creating ? 'New company / person' : s.company_name}</h3>
        {s.is_practice && (
          <p className="text-xs text-muted-foreground mb-3" data-testid="sup-practice-note">
            These are i3&apos;s own details — used whenever i3 sends an invoice, or receives one.
          </p>
        )}
        <label className="block mb-3" data-testid="sup-type-wrapper">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Supplier type — controls which quick-add chips show on the invoice editor
          </span>
          <select value={s.type || 'teleradiologist'}
            onChange={e => setS({ ...s, type: e.target.value })}
            data-testid="sup-type"
            className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background">
            <option value="teleradiologist">Teleradiologist — per-study reporting (reporting fee %)</option>
            <option value="locum">Locum — hours worked + travel + misc</option>
            <option value="generic">Generic — free-form lines only</option>
          </select>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <SupField label="Company name *" k="company_name" s={s} setS={setS} />
          <SupField label="Doctor name"    k="doctor_name"  s={s} setS={setS} />
          <SupField label="Address line 1" k="address_line1" s={s} setS={setS} />
          <SupField label="Address line 2" k="address_line2" s={s} setS={setS} />
          <SupField label="City"     k="city"     s={s} setS={setS} />
          <SupField label="State"    k="state"    s={s} setS={setS} />
          <SupField label="Postcode" k="postcode" s={s} setS={setS} />
          <SupField label="Country"  k="country"  s={s} setS={setS} />
          <SupField label="ABN / Tax ID" k="abn" s={s} setS={setS} />
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              GST rate (%) — user-definable · e.g. 0 for exports, 10 for AU, 18 for India
            </span>
            <Input type="number" step="0.01" min="0" max="100"
              value={s.gst_rate == null ? '' : (s.gst_rate * 100).toString()}
              onChange={e => {
                const raw = e.target.value;
                setS({ ...s, gst_rate: raw === '' ? 0 : (parseFloat(raw) / 100) });
              }}
              data-testid="sup-gst_rate"
              placeholder="0 for GST-free exports" />
            <p className="text-[10px] text-muted-foreground mt-1">
              Currently stored as <strong>{((s.gst_rate ?? 0) * 100).toFixed(2)}%</strong>. Enter 0 for GST-free.
            </p>
          </label>
          <SupField label="Email" k="email" s={s} setS={setS} type="email" />
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Invoice number prefix — their own running number
            </span>
            <Input value={s.invoice_prefix || ''} maxLength={6} placeholder="Auto from name"
              onChange={e => setS({ ...s, invoice_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
              data-testid="sup-invoice_prefix" />
            <p className="text-[10px] text-muted-foreground mt-1">
              Invoices sent by this company are numbered <strong>{s.invoice_prefix || 'PREFIX'}-{new Date().getFullYear()}-00001</strong>, -00002…
            </p>
          </label>
        </div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-4 mb-2">Bank details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <SupField label="Bank name"       k="bank_name"         s={s} setS={setS} />
          <SupField label="Account name"    k="bank_account_name" s={s} setS={setS} />
          <SupField label="BSB"             k="bsb"               s={s} setS={setS} />
          <SupField label="Account number"  k="account_number"    s={s} setS={setS} />
          <SupField label="SWIFT (overseas)" k="swift"            s={s} setS={setS} />
          <SupField label="IBAN (overseas)"  k="iban"             s={s} setS={setS} />
        </div>
        <label className="block mt-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Notes</span>
          <Textarea value={s.notes || ''} onChange={e => setS({ ...s, notes: e.target.value })}
            data-testid="sup-notes" className="min-h-[60px]" />
        </label>
        <div className="flex justify-between mt-4">
          <div>
            {!creating && !s.is_practice && (
              <button onClick={remove} className="text-xs font-semibold text-red-600 border border-red-300 px-2.5 py-1.5 rounded"
                data-testid="sup-delete">
                <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs font-semibold px-3 py-1.5 rounded border">Cancel</button>
            <button onClick={save} disabled={busy || !s.company_name} data-testid="sup-save"
              className="text-sm font-semibold px-4 py-2 rounded bg-teal-600 text-white inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice editor ────────────────────────────────────────────────
export function InvoiceEditorPage() {
  const { invnum } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const load = useCallback(async () => {
    const [r, s, c] = await Promise.all([
      axios.get(`${API}/invoices/${invnum}`,           { withCredentials: true }),
      axios.get(`${API}/invoices/suppliers`,           { withCredentials: true }),
      axios.get(`${API}/invoices/service-codes`,       { withCredentials: true }),
    ]);
    setInv(r.data); setSuppliers(s.data.suppliers || []); setCodes(c.data.codes || []);
  }, [invnum]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload, msg='Saved') => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/invoices/${invnum}`, payload, { withCredentials: true });
      setInv(r.data); setSavedMsg(msg);
      if (r.data.invoice_number !== invnum) navigate(`/invoices/${r.data.invoice_number}`, { replace: true });
      setTimeout(() => setSavedMsg(''), 2000);
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const addLine = (prefill = {}) => {
    // Truly blank line by default — locum/misc chips pass a prefill.
    const lines = [...(inv.line_items || []), prefill];
    patch({ line_items: lines });
  };
  // Iter 155 — quick-add prefills for locum & misc invoices. Rates are ATO
  // defaults the user can overwrite in-line; qty stays blank so the locum
  // fills in the number of hours / km / litres for this specific invoice.
  const QUICK_ADDS = {
    locum: [
      { label: '+ Hours worked',    line: { qty: null, unit_price: null, description: 'Hours worked',            code: 'Other' } },
      { label: '+ Km (ATO 88c)',    line: { qty: null, unit_price: 0.88, description: 'Kilometres travelled (ATO cents-per-km)', code: 'Transport Allowance' } },
      { label: '+ Km (custom)',     line: { qty: null, unit_price: null, description: 'Kilometres travelled',    code: 'Transport Allowance' } },
      { label: '+ Fuel (litres)',   line: { qty: null, unit_price: null, description: 'Fuel (litres × $/L)',     code: 'Transport Allowance' } },
      { label: '+ Accommodation',   line: { qty: null, unit_price: null, description: 'Accommodation (nights × $/night)', code: 'Accommodation Allowance' } },
      { label: '+ Misc',            line: { qty: null, unit_price: null, description: 'Miscellaneous',           code: 'Other' } },
      { label: '+ Blank line',      line: {} },
    ],
    generic: [
      { label: '+ Transport',    line: { qty: null, unit_price: null, description: 'Transport allowance',    code: 'Transport Allowance' } },
      { label: '+ Accommodation',line: { qty: null, unit_price: null, description: 'Accommodation allowance',code: 'Accommodation Allowance' } },
      { label: '+ Misc',         line: { qty: null, unit_price: null, description: 'Miscellaneous',          code: 'Other' } },
      { label: '+ Blank line',   line: {} },
    ],
    teleradiologist: [],
  };
  // Debounced line-item editor: local optimistic state + PATCH only after
  // 400ms of idle so rapid keystrokes don't race each other.
  const setLine = (idx, key, value) => {
    const numericKeys = ['qty', 'unit_price', 'extended_price'];
    const parsed = numericKeys.includes(key)
      ? (value === '' || value == null ? null : parseFloat(value))
      : value;
    setInv(prev => {
      const lines = [...(prev.line_items || [])];
      lines[idx] = { ...lines[idx], [key]: parsed };
      // Recompute amount preview: extended_price wins if set
      if (lines[idx].extended_price != null && !Number.isNaN(lines[idx].extended_price)) {
        lines[idx].amount = Math.round(lines[idx].extended_price * 100) / 100;
      } else {
        const q = parseFloat(lines[idx].qty || 0);
        const p = parseFloat(lines[idx].unit_price || 0);
        const fee = prev.reporting_fee_pct;
        const base = q * p;
        lines[idx].amount = Math.round(base * (fee != null ? fee / 100 : 1) * 100) / 100;
      }
      if (window.__i3InvoiceSaveTimer) clearTimeout(window.__i3InvoiceSaveTimer);
      const snapshot = lines;
      window.__i3InvoiceSaveTimer = setTimeout(() => {
        patch({ line_items: snapshot }, '');
      }, 400);
      return { ...prev, line_items: lines };
    });
  };
  const removeLine = (idx) => {
    const lines = (inv.line_items || []).filter((_, i) => i !== idx);
    patch({ line_items: lines });
  };
  const downloadPdf = async () => {
    const r = await axios.get(`${API}/invoices/${invnum}/pdf`, {
      withCredentials: true, responseType: 'blob',
    });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${invnum}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const duplicate = async () => {
    const r = await axios.post(`${API}/invoices/${invnum}/duplicate`, {}, { withCredentials: true });
    navigate(`/invoices/${r.data.invoice_number}`);
  };
  const del = async () => {
    if (!window.confirm(`Delete ${invnum}?`)) return;
    await axios.delete(`${API}/invoices/${invnum}`, { withCredentials: true });
    navigate('/invoices');
  };

  if (!inv) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const sup = inv.supplier_snapshot || {};
  // Iter 155 — use the live supplier record for `type` (snapshot is captured at
  // invoice creation and may pre-date the type field on old invoices).
  const liveSupplier = suppliers.find(x => x.supplier_id === inv.supplier_id) || {};
  const supplierType = liveSupplier.type || 'teleradiologist';
  const quickAdds = QUICK_ADDS[supplierType] || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-5 md:p-8">
        <Link to="/invoices" className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> All invoices
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#0d2644' }} data-testid="invoice-number">{inv.invoice_number}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Status: <strong>{inv.status}</strong>{inv.paid_date && ` · Paid ${inv.paid_date}`}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {savedMsg && <span className="text-xs text-teal-700 font-semibold" data-testid="saved-msg">{savedMsg}</span>}
            <button onClick={downloadPdf} data-testid="btn-pdf" className="text-sm font-semibold inline-flex items-center gap-2 px-3 py-1.5 rounded border">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={duplicate} data-testid="btn-duplicate" className="text-sm font-semibold inline-flex items-center gap-2 px-3 py-1.5 rounded border">
              <Copy className="w-4 h-4" /> Duplicate
            </button>
            <button onClick={del} data-testid="btn-delete" className="text-xs text-red-600 border border-red-300 px-2.5 py-1.5 rounded">
              <Trash2 className="w-3.5 h-3.5 inline" /> Delete
            </button>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-4 mb-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_200px] gap-3" data-testid="invoice-parties">
          <PartySelect label="From (who is billing)" value={inv.supplier_id} parties={suppliers}
            exclude={inv.recipient_id || 'sup_practice'} snap={sup} testid="supplier-select"
            onChange={v => patch({ supplier_id: v }, 'Sender updated')}
            hint={inv.status === 'draft' ? 'Changing the sender moves this draft into their number series.' : ''} />
          <PartySelect label="To (who is being billed)" value={inv.recipient_id || 'sup_practice'} parties={suppliers}
            exclude={inv.supplier_id} snap={inv.recipient_snapshot || {}} testid="recipient-select"
            onChange={v => patch({ recipient_id: v }, 'Recipient updated')} />
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Invoice date</span>
            <Input type="date" value={inv.invoice_date} onChange={e => patch({ invoice_date: e.target.value }, '')} data-testid="invoice-date" />
          </label>
        </div>

        <div className="rounded-xl border bg-card p-4 mb-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0d2644' }}>Line items</h3>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="supplier-type-badge">
                {supplierType}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {supplierType === 'teleradiologist' && (
                <label className="text-xs inline-flex items-center gap-1.5">
                  <span className="text-muted-foreground">Reporting fee %</span>
                  <Input type="number" step="0.5" min="0" max="100"
                    value={inv.reporting_fee_pct == null ? '' : inv.reporting_fee_pct}
                    onChange={e => patch({ reporting_fee_pct: e.target.value === '' ? null : parseFloat(e.target.value) }, 'Fee % updated')}
                    data-testid="invoice-reporting-fee"
                    className="w-20 h-8 text-right" placeholder="e.g. 60" />
                </label>
              )}
              {quickAdds.map(qa => (
                <button key={qa.label} onClick={() => addLine(qa.line)}
                  data-testid={`quick-add-${qa.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '')}`}
                  className="text-xs font-semibold px-2.5 py-1 rounded border hover:bg-muted transition">
                  {qa.label}
                </button>
              ))}
              {quickAdds.length === 0 && (
                <button onClick={() => addLine()} data-testid="add-line" className="text-xs font-semibold inline-flex items-center gap-1.5 px-2.5 py-1 rounded border">
                  <Plus className="w-3.5 h-3.5" /> Add line
                </button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mb-2">
            {supplierType === 'locum'
              ? <>Locum quick-adds pre-fill the ATO 88c/km rate — overwrite unit price if the locum charges a different rate. Type an <strong>Extended price</strong> to override qty × unit-price for a specific line.</>
              : supplierType === 'generic'
                ? <>Add lines freely — type qty × unit price, or type an <strong>Extended price</strong> for a fixed amount.</>
                : <>Type an <strong>Extended price</strong> to override the qty × unit-price math. Reporting fee % (if set) applies to lines that use qty × unit-price. Blank rows stay blank on the PDF.</>
            }
          </p>
          <table className="w-full text-sm" data-testid="line-items-table">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 w-16">Qty</th>
                <th className="text-left py-2">Description</th>
                <th className="text-left py-2 w-40">Code</th>
                <th className="text-right py-2 w-24">Unit $</th>
                <th className="text-right py-2 w-28">Extended $</th>
                <th className="text-right py-2 w-24">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(inv.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-t" data-testid={`line-${idx}`}>
                  <td className="py-1 pr-2">
                    <Input type="number" step="0.5" value={li.qty ?? ''} onChange={e => setLine(idx, 'qty', e.target.value)} data-testid={`line-${idx}-qty`} className="w-16" placeholder="" />
                  </td>
                  <td className="py-1 pr-2">
                    <Input value={li.description ?? ''} onChange={e => setLine(idx, 'description', e.target.value)} data-testid={`line-${idx}-desc`} placeholder="" />
                  </td>
                  <td className="py-1 pr-2">
                    <select value={li.code || ''} onChange={e => setLine(idx, 'code', e.target.value)}
                      data-testid={`line-${idx}-code`} className="w-full text-sm border rounded px-2 py-1.5 bg-background">
                      <option value="">—</option>
                      {codes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <Input type="number" step="0.01" value={li.unit_price ?? ''} onChange={e => setLine(idx, 'unit_price', e.target.value)} data-testid={`line-${idx}-price`} className="text-right" placeholder="" />
                  </td>
                  <td className="py-1 pr-2">
                    <Input type="number" step="0.01" value={li.extended_price ?? ''} onChange={e => setLine(idx, 'extended_price', e.target.value)} data-testid={`line-${idx}-extended`} className="text-right" placeholder="" />
                  </td>
                  <td className="py-1 pr-2 text-right font-semibold" data-testid={`line-${idx}-amount`}>${fmt(li.amount)}</td>
                  <td>
                    <button onClick={() => removeLine(idx)} className="text-red-600" data-testid={`line-${idx}-remove`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {(inv.line_items || []).length === 0 && (
                <tr><td colSpan="7" className="text-center py-4 text-muted-foreground text-xs">
                  No lines. Tap a <strong>+ button</strong> above to add one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: '#0d2644' }}>Payment</h3>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Paid date</span>
              <Input type="date" value={inv.paid_date || ''} onChange={e => patch({ paid_date: e.target.value }, 'Payment recorded')} data-testid="paid-date" />
              <p className="text-[10px] text-muted-foreground mt-1">Setting a paid date auto-flips status to <strong>paid</strong>.</p>
            </label>
            <label className="block mt-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Status</span>
              <select value={inv.status} onChange={e => patch({ status: e.target.value }, 'Status updated')}
                data-testid="invoice-status" className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background">
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="void">Void</option>
              </select>
            </label>
            <label className="block mt-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Notes</span>
              <Textarea value={inv.notes || ''} onChange={e => setInv({ ...inv, notes: e.target.value })}
                onBlur={() => patch({ notes: inv.notes }, '')}
                data-testid="invoice-notes" className="min-h-[80px]" />
            </label>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: '#0d2644' }}>Totals</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold" data-testid="subtotal">${fmt(inv.subtotal)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">GST rate (%)</span>
                <span className="inline-flex items-center gap-1.5">
                  <Input type="number" step="0.01" min="0" max="100"
                    value={((inv.gst_rate ?? 0) * 100).toString()}
                    onChange={e => {
                      const pct = parseFloat(e.target.value || 0);
                      patch({ override_gst_rate: pct / 100 }, 'GST updated');
                    }}
                    data-testid="invoice-gst-rate"
                    className="w-20 text-right h-8 text-sm" />
                  <span className="text-xs text-muted-foreground">%</span>
                </span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST amount</span><span className="font-semibold" data-testid="gst-total">${fmt(inv.gst)}</span></div>
              <div className="flex justify-between border-t pt-2 mt-2 text-lg font-bold" style={{ color: '#0d2644' }}>
                <span>Total</span><span data-testid="grand-total">${fmt(inv.total)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                GST rate pulls from the sender (From) at invoice creation. You can override it here for this specific invoice — leave at 0 for GST-free / export invoices.
              </p>
            </div>
            {busy && <p className="text-xs text-muted-foreground mt-3"><Loader2 className="w-3 h-3 animate-spin inline" /> Saving…</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PartySelect({ label, value, parties, exclude, snap = {}, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background">
        {parties.filter(p => p.supplier_id !== exclude).map(p => (
          <option key={p.supplier_id} value={p.supplier_id}>
            {p.company_name}{p.is_practice ? ' (i3)' : ''} · {Number(((p.gst_rate || 0) * 100).toFixed(2))}% GST
          </option>
        ))}
      </select>
      <p className="text-[10px] text-muted-foreground mt-1">
        {[snap.doctor_name, [snap.city, snap.state, snap.country].filter(Boolean).join(', '), snap.abn && `ABN ${snap.abn}`].filter(Boolean).join(' · ')}
        {hint && <span className="block">{hint}</span>}
      </p>
    </label>
  );
}

function NewInvoiceDialog({ parties, busy, onClose, onCreate }) {
  const practice = parties.find(p => p.is_practice) || parties[0];
  const firstOther = parties.find(p => p.supplier_id !== practice.supplier_id);
  const [fromId, setFromId] = useState(firstOther.supplier_id);
  const [toId, setToId] = useState(practice.supplier_id);
  const swap = () => { setFromId(toId); setToId(fromId); };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-invoice-dialog">
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-1" style={{ color: '#0d2644' }}>New invoice</h3>
        <p className="text-xs text-muted-foreground mb-4">Choose who is billing and who is being billed. You can change either later while it&apos;s a draft.</p>
        <PartySelect label="From (who is billing)" value={fromId} parties={parties} exclude={toId}
          onChange={setFromId} testid="new-invoice-from" />
        <button onClick={swap} type="button" data-testid="new-invoice-swap"
          className="my-2 text-xs font-semibold px-2.5 py-1 rounded border inline-flex items-center gap-1">
          <ArrowLeftRight className="w-3.5 h-3.5" /> Swap
        </button>
        <PartySelect label="To (who is being billed)" value={toId} parties={parties} exclude={fromId}
          onChange={setToId} testid="new-invoice-to" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs font-semibold px-3 py-1.5 rounded border" data-testid="new-invoice-cancel">Cancel</button>
          <button onClick={() => onCreate(fromId, toId)} disabled={busy || fromId === toId} data-testid="new-invoice-create"
            className="text-sm font-semibold px-4 py-2 rounded bg-teal-600 text-white inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create invoice
          </button>
        </div>
      </div>
    </div>
  );
}
